import {
  type Log,
  LogLevels,
  type LoggerConfiguration,
  type LogLevel as OurLogLevel,
} from '@backmarket/front-logger'
import { LogLevel, faro } from '@grafana/faro-web-sdk'

const faroLogLevels: Record<OurLogLevel, LogLevel> = {
  [LogLevels.DEBUG]: LogLevel.DEBUG,
  [LogLevels.INFO]: LogLevel.INFO,
  [LogLevels.ERROR]: LogLevel.ERROR,
}

// Faro's LogContext is Record<string, string> — it calls stringifyObjectValues()
// internally, which would turn nested objects into "[object Object]". We
// pre-serialize here so nested attributes arrive as readable JSON strings in
// Grafana rather than as unqueryable noise.
function toFaroContext(
  attributes: Record<string, unknown>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attributes)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)]),
  )
}

export function createFaroTransport({
  level,
}: { level?: OurLogLevel } = {}): LoggerConfiguration['transports'][number] {
  return {
    level,
    name: 2,
    log(transformedLog: Log) {
      // The Faro singleton is initialised by nuxt-module-instrumentation's
      // client plugin, which runs after the logger plugin. Guard against the
      // window check (SSR safety) and against the logger emitting before Faro
      // has finished initialising.
      if (typeof window === 'undefined') return
      if (!faro?.api) return

      const logLevel = LogLevels[transformedLog.SeverityText]
      const context = toFaroContext(transformedLog.Attributes)

      if (logLevel === LogLevels.ERROR && transformedLog.error) {
        // pushError sends a dedicated error event with a full stack trace,
        // which Grafana surfaces separately from log lines. We still call
        // pushLog below so the error also appears in the log stream with its
        // structured context.
        const { name, message, stack, cause } = transformedLog.error
        const error = Object.assign(new Error(message), { name, stack, cause })
        faro.api.pushError(error, { context })
      }

      faro.api.pushLog([transformedLog.Body], {
        level: faroLogLevels[logLevel],
        context,
      })
    },
  }
}
